/**
 * Verification: who has put their name to a link, and what a reader may do.
 *
 * This slice decides which acts an entry can honestly offer (`verificationFor`),
 * what the record already holds about it (`stateFor`, `confirmationStrip`),
 * how acts queued against an unsent draft are recorded and taken back, and how
 * a typed party becomes a request body. Counts and positions are all it ever
 * says — there is no threshold at which the registry decides, and nothing here
 * turns a tally into a verdict.
 */
import type {
  AttestationStance,
  ProvenanceAttestation,
  ProvenanceComment,
  ProvenanceEntry,
  ProvenanceVerificationRequest,
  RequestVerificationRequest,
} from "@ondo/shared";

import { EMAIL_PATTERN, ONDO_ID_PATTERN } from "../lib/constants.ts";
import {
  CONFIRMATION_COPY,
  MAX_ACTS_PER_ENTRY,
  PROVENANCE_COPY,
  VERIFICATION_COMPOSERS,
  VERIFICATION_LABELS,
  VERIFICATION_STATE_COPY,
} from "./provenance.constants.ts";
import type { ComposerModel } from "./provenance.constants.ts";
import { formatActDate } from "./provenance.dates.ts";
import { setEntryField } from "./provenance.draft.ts";
import type { PendingActs, PendingParty, ProvenanceItem } from "./provenance.draft.ts";
import type { SurfaceConfig, VerificationOffer } from "./provenance.surfaces.ts";

/**
 * What a caller is trying to do to an entry.
 *
 * A union rather than a key and a loose string, because the three that carry
 * something carry different things — a dispute and a comment carry a body, an
 * ask carries an ONDO id — and a single `(key, value)` signature would let a
 * view send an ONDO id as a dispute.
 */
export type VerificationIntent =
  | { key: "verify" }
  | { key: "dispute"; body: string }
  | { key: "comment"; body: string }
  | { key: "request"; party: string };

export interface VerificationAction {
  key: "verify" | "dispute" | "comment" | "request";
  label: string;
  available: boolean;
  /** Why not, when not. Never null when `available` is false. */
  unavailable: string | null;
  /**
   * Null when this action is not a toggle; true or false when it is one and
   * which way. Rendered straight onto `aria-pressed`, so the view does not have
   * to know that Verify is the only toggle in the row.
   */
  pressed: boolean | null;
  /** What this action has already recorded, or null. */
  taken: string | null;
  /** What has to be typed before it can be recorded, or null for one click. */
  compose: ComposerModel | null;
}

/** Something recorded against a draft and not yet sent. */
export interface QueuedAct {
  id: string;
  kind: "comment" | "request";
  /** 'Comment' / 'Verification asked of'. */
  label: string;
  /** The body, or the ONDO id. */
  detail: string;
  /** The accessible name of the ✕ that takes it back. */
  removeLabel: string;
}

export interface VerificationModel {
  actions: VerificationAction[];
  /** One sentence above the row, when there is one worth saying. */
  note: string | null;
  /** Recorded here and not yet sent. Always empty on a recorded entry. */
  queued: QueuedAct[];
}

/**
 * One person who was asked to look at this entry, and whether they have.
 *
 * The asker's side only. `request_provenance_verification` guards on
 * `can_curate`, so `openRequestCount` is non-zero only for whoever sent it —
 * naming them here discloses nothing a reader could not already have.
 */
export interface AskRow {
  id: string;
  /** 'Beyeler Foundation · ONDO-4001'. */
  who: string;
  /** 'asked 12 Jan 2026'. */
  when: string;
  /** 'Awaiting an answer' / 'Declined'. Never read as a finding. */
  status: string;
}

export interface AskedModel {
  /** '2 people asked.' Rendered off the count, before any names arrive. */
  summary: string;
  /** That silence concludes nothing. Said once, above the list. */
  note: string;
  /** Empty until the fetch lands, and empty is not "nobody". */
  rows: AskRow[];
}

/**
 * What the record holds about this link, said rather than offered.
 *
 * Counts and positions, never a verdict — there is no threshold at which this
 * registry starts deciding, and the panel that replaced the buttons must not be
 * where one appears.
 */
export interface VerificationState {
  /** '2 confirmations · 1 dispute', or the sentence for nobody having spoken. */
  standing: string;
  /** Said only when confirmations and disputes both exist. */
  caveat: string | null;
  /**
   * Your own live position, or null.
   *
   * Null says nothing rather than "you have not taken a position": on a surface
   * with no button, that would be a nudge toward a control that is not there.
   */
  yours: string | null;
  asked: AskedModel | null;
}

/**
 * How an entry presents its verification, as a shape rather than as values.
 *
 * The surface config decides which branch, and it has to be a different SHAPE:
 * if a narrowed surface merely returned `actions: []`, `ProvenanceVerification`
 * would need `actions.length > 0 &&` to avoid rendering a heading over nothing
 * — a product rule back in a view — and an empty array could not carry the
 * statement that replaces the buttons.
 *
 * `acts` is null on the state branch when nothing the surface offers is
 * available to this reader. That nullability IS the "let the server decide"
 * rule: the config says which controls a surface would ever show, and the
 * server's answer decides whether any of them survive.
 */
export type VerificationPanel =
  | { mode: "act"; acts: VerificationModel }
  | { mode: "state"; state: VerificationState; acts: VerificationModel | null };

/**
 * Unmaking an entry: removing a draft, or withdrawing one from the record.
 *
 * Not a verification act, which is why it no longer lives in that panel. The
 * caller supplies neither label nor sentence — both are decided here, because
 * which of the two acts this is follows from the entry, not from the screen.
 */
export interface EntryRemoval {
  label: string;
  /**
   * What this does to what is already on the record. Null on a draft, where
   * there is nothing on the record for it to do anything to.
   */
  note: string | null;
}

/**
 * The strip across the foot of a card: the one line about who stands behind it.
 *
 * `tone` decides the colour and `pill` the word, and they are decided together
 * here rather than in CSS — a dispute must take the strip out of green whatever
 * the confirmations say, and that is a rule about the record rather than a
 * style.
 */
export interface ConfirmationStrip {
  tone: "confirmed" | "disputed" | "unconfirmed" | "draft";
  pill: string;
  /** '2 confirmations — open to see who and when'. Always says how many. */
  summary: string;
}

/** One person's position on one link, as the expansion shows it. */
export interface AttestationRowModel {
  id: string;
  stance: AttestationStance;
  /** '✓ CONFIRMED' / '⚠ DISPUTED'. */
  stanceLabel: string;
  byName: string;
  byOndoId: string;
  /** '12 Jan 2026'. */
  date: string;
  /** Optional on a confirmation, required on a dispute. */
  body: string | null;
  /** Taken back. The row stays, because the position was held. */
  withdrawn: boolean;
  mine: boolean;
}

export interface CommentRowModel {
  id: string;
  byName: string;
  byOndoId: string;
  date: string;
  body: string;
  withdrawn: boolean;
  mine: boolean;
}

/**
 * What the server said when the card was opened.
 *
 * Fetched on expansion rather than with the chain: the counts are already on
 * the entry, and pulling every attestation and comment for forty links to
 * render six lines nobody has opened is forty requests for nothing.
 */
export type ConfirmationLoad =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; attestations: ProvenanceAttestation[]; comments: ProvenanceComment[] };

export type ConfirmationDetail =
  | { kind: "idle" }
  | { kind: "loading"; message: string }
  | { kind: "failed"; message: string }
  | {
      kind: "loaded";
      attestations: AttestationRowModel[];
      comments: CommentRowModel[];
      /** Said when both lists are empty, so a blank panel is never the answer. */
      emptyNote: string | null;
    };

/**
 * Which verification actions this entry can honestly offer, and what a taken one
 * says.
 *
 * Three cases, and none of them is a refusal to act:
 *
 * A DRAFT is yours and unsent. Provenance is written by one person and that
 * person is the first to put their name to it, so Verify is open here — what it
 * records is filed with the entry at submit and never before. Dispute is the one
 * that is not open, and NOT because of who wrote it: a draft is still editable,
 * so disagreeing with it is an edit.
 *
 * A RECORDED entry the caller may act on. Verify, dispute and comment are open
 * to anyone who can read it, its author included. Verify and Dispute close once
 * a position is held — `provenance_attestations_one_live` allows one each, so
 * offering the button again is a 409 waiting to happen. Asking stays the
 * author's, because `request_provenance_verification` still guards on
 * `can_curate`: it is a nudge rather than a gate now, but it is still theirs.
 *
 * A RECORDED entry the caller may only read. `canAttest` false no longer means
 * "was not asked" — it means this seat reads and does not sign. Said as that.
 */
export function verificationFor(item: ProvenanceItem): VerificationModel {
  if (item.kind === "draft") {
    const acts = item.entry.acts;
    const verified = acts.stance === "verify";
    const commentsFull = acts.comments.length >= MAX_ACTS_PER_ENTRY;
    const requestsFull = acts.requests.length >= MAX_ACTS_PER_ENTRY;

    return {
      note: PROVENANCE_COPY.actsPending,
      queued: queuedActs(acts),
      actions: [
        {
          key: "verify",
          label: VERIFICATION_LABELS.verify,
          available: true,
          unavailable: null,
          pressed: verified,
          taken: verified ? PROVENANCE_COPY.verifyQueued : null,
          compose: null,
        },
        {
          key: "dispute",
          label: VERIFICATION_LABELS.dispute,
          available: false,
          unavailable: PROVENANCE_COPY.disputeOwnDraft,
          pressed: null,
          taken: null,
          compose: null,
        },
        {
          key: "comment",
          label: VERIFICATION_LABELS.comment,
          available: !commentsFull,
          unavailable: commentsFull ? PROVENANCE_COPY.actsFull : null,
          pressed: null,
          taken: acts.comments.length > 0 ? PROVENANCE_COPY.commentQueued : null,
          compose: VERIFICATION_COMPOSERS.comment,
        },
        {
          key: "request",
          label: VERIFICATION_LABELS.request,
          available: !requestsFull,
          unavailable: requestsFull ? PROVENANCE_COPY.actsFull : null,
          pressed: null,
          taken: acts.requests.length > 0 ? PROVENANCE_COPY.requestQueued : null,
          compose: VERIFICATION_COMPOSERS.request,
        },
      ],
    };
  }

  const entry = item.entry;
  // Two different closures with two different sentences. "This seat does not
  // sign" and "you already signed" are not the same fact, and the version of
  // this function that shared one message between them is what made a working
  // feature read as a broken one.
  const positionReason =
    !entry.canAttest
      ? PROVENANCE_COPY.readOnlySeat
      : entry.myStance !== null
        ? PROVENANCE_COPY.positionHeld
        : null;

  return {
    note: null,
    queued: [],
    actions: [
      {
        key: "verify",
        label: VERIFICATION_LABELS.verify,
        available: positionReason === null,
        unavailable: positionReason,
        pressed: null,
        taken: entry.myStance === "verify" ? PROVENANCE_COPY.verifiedByYou : null,
        compose: null,
      },
      {
        key: "dispute",
        label: VERIFICATION_LABELS.dispute,
        available: positionReason === null,
        unavailable: positionReason,
        pressed: null,
        taken: entry.myStance === "dispute" ? PROVENANCE_COPY.disputedByYou : null,
        compose: VERIFICATION_COMPOSERS.dispute,
      },
      // Open to anyone who can read the entry, and repeatable. Saying "the
      // Beyeler archive dates this to 1961" is context rather than a position,
      // so nothing here is marked taken and nothing closes after one.
      {
        key: "comment",
        label: VERIFICATION_LABELS.comment,
        available: true,
        unavailable: null,
        pressed: null,
        taken: null,
        compose: VERIFICATION_COMPOSERS.comment,
      },
      {
        key: "request",
        label: VERIFICATION_LABELS.request,
        available: entry.canCurate,
        unavailable: entry.canCurate ? null : PROVENANCE_COPY.askIsYours,
        pressed: null,
        taken: null,
        compose: VERIFICATION_COMPOSERS.request,
      },
    ],
  };
}

function queuedActs(acts: PendingActs): QueuedAct[] {
  return [
    ...acts.comments.map((comment) => ({
      id: comment.id,
      kind: "comment" as const,
      label: PROVENANCE_COPY.queuedComment,
      detail: comment.body,
      removeLabel: PROVENANCE_COPY.removeComment,
    })),
    ...acts.requests.map((request) => ({
      id: request.id,
      kind: "request" as const,
      label: PROVENANCE_COPY.queuedRequest,
      detail: request.party.value,
      removeLabel: PROVENANCE_COPY.removeRequest,
    })),
  ];
}

/**
 * The surface's only power over an action row: to drop one.
 *
 * A filter, and that is the whole of the guarantee. This constructs no
 * `VerificationAction`, so it cannot set `available`, cannot clear
 * `unavailable`, and cannot invent a control — every action it returns is the
 * identical object `verificationFor` built from the database's own answer.
 *
 * Note the two signatures. `verificationFor` sees the item and has no idea what
 * a surface is; this sees the surface and never sees an item. Neither has both
 * in scope, so there is nowhere to write `config.acts.includes(key) || …`.
 */
function offeredHere(
  model: VerificationModel,
  acts: readonly VerificationAction["key"][],
): VerificationModel {
  return { ...model, actions: model.actions.filter((action) => acts.includes(action.key)) };
}

/** The controls this reader may actually use, or null when that is none of them. */
function availableHere(model: VerificationModel): VerificationModel | null {
  const actions = model.actions.filter((action) => action.available);
  return actions.length === 0 ? null : { ...model, actions };
}

/**
 * How an entry presents its verification on this surface.
 *
 * The `act` branch shows every offered control and lets a closed one carry its
 * reason — a collector filling in a form is better served by "this is why you
 * cannot dispute your own draft" than by a button that silently is not there.
 *
 * The `state` branch drops closed controls instead, and does not lose their
 * reasons: "you verified this entry" and "this account reads and does not sign"
 * are sentences in the statement above. What is left is what this reader can
 * do, which on your own work is ask somebody to look at it, and on a work
 * shared with an expert is sign it.
 */
export function panelFor(
  item: ProvenanceItem,
  offer: VerificationOffer,
  asked: AskedModel | null,
): VerificationPanel {
  const offered = offeredHere(verificationFor(item), offer.acts);

  if (offer.mode === "act") return { mode: "act", acts: offered };

  return {
    mode: "state",
    state: stateFor(item, asked),
    acts: availableHere(offered),
  };
}

/**
 * What the record holds, in words.
 *
 * `describeConfirmations` is reused rather than re-counted, so the strip on the
 * shut card and the statement inside it cannot come to disagree.
 */
export function stateFor(item: ProvenanceItem, asked: AskedModel | null): VerificationState {
  if (item.kind === "draft") {
    // Unreachable through `panelFor`, whose draft offer is always `act` — a
    // draft has no standing to state. Answered rather than thrown so a future
    // config cannot turn a shape mistake into a blank screen.
    return {
      standing: CONFIRMATION_COPY.draftNote,
      caveat: null,
      yours: null,
      asked: null,
    };
  }

  const entry = item.entry;
  const counted = describeConfirmations(entry.verifiedByCount, entry.disputedByCount);
  const contested = entry.verifiedByCount > 0 && entry.disputedByCount > 0;

  return {
    standing: counted ?? VERIFICATION_STATE_COPY.none,
    caveat: contested ? VERIFICATION_STATE_COPY.contested : null,
    yours: describeMyStance(entry.myStance),
    asked,
  };
}

function describeMyStance(stance: AttestationStance | null): string | null {
  if (stance === "verify") return PROVENANCE_COPY.verifiedByYou;
  if (stance === "dispute") return PROVENANCE_COPY.disputedByYou;
  return null;
}

/**
 * Who has been asked and not answered.
 *
 * Null unless this caller is the one who asked. `openRequestCount` is only ever
 * non-zero for somebody who `canCurate` — asking guards on it — so the second
 * half of that test is belt and braces against a server that started answering
 * more generously than it does today.
 */
export function askedFor(
  entry: ProvenanceEntry,
  requests: ProvenanceVerificationRequest[] | undefined,
): AskedModel | null {
  if (!entry.canCurate || entry.openRequestCount === 0) return null;

  return {
    summary: describeAsks(entry.openRequestCount),
    note: VERIFICATION_STATE_COPY.askedNote,
    // Empty until the fetch lands. The count above is already true, which is
    // the same counts-first, names-on-expand shape the strip uses.
    rows: (requests ?? []).filter((request) => request.endedAt === null).map(toAskRow),
  };
}

function toAskRow(request: ProvenanceVerificationRequest): AskRow {
  return {
    id: request.id,
    who: `${request.requestedName} · ${request.requestedOndoId}`,
    when: `asked ${formatActDate(request.createdAt)}`,
    status:
      request.outcome === "declined"
        ? VERIFICATION_STATE_COPY.askedDeclined
        : VERIFICATION_STATE_COPY.askedAwaiting,
  };
}

/** '1 person asked' / '3 people asked'. Beside describeConfirmations, not in copy. */
export function describeAsks(count: number): string {
  return count === 1 ? "1 person asked." : `${String(count)} people asked.`;
}

/**
 * Withdrawing a recorded entry, or null.
 *
 * The config appears only to the LEFT of the `&&`, which is the invariant that
 * keeps a surface unable to widen: it can replace a value with null and it can
 * never construct one. `canCurate` is the server's answer and decides the rest.
 *
 * A DRAFT has no equivalent here on purpose — removing one is unconditional,
 * because it lives in this browser and nowhere else, so there is nothing for a
 * surface or a policy to have an opinion about.
 */
export function withdrawalFor(
  entry: ProvenanceEntry,
  surface: SurfaceConfig,
): EntryRemoval | null {
  if (!surface.withdraw || !entry.canCurate) return null;

  return { label: PROVENANCE_COPY.withdrawEntry, note: PROVENANCE_COPY.withdrawNote };
}

/**
 * Records an act against a local draft.
 *
 * Returns the problem sentence rather than throwing, matching `attachOndoId`,
 * and returns the list BY IDENTITY for anything that is not a draft — the same
 * non-path `setEntryField` relies on.
 */
export function recordAct(
  items: ProvenanceItem[],
  id: string,
  intent: VerificationIntent,
): { items: ProvenanceItem[]; problem: string | null } {
  const target = items.find((item) => item.kind === "draft" && item.entry.id === id);
  if (!target || target.kind !== "draft") {
    return { items, problem: PROVENANCE_COPY.notADraft };
  }

  const acts = target.entry.acts;

  switch (intent.key) {
    case "verify":
      // A toggle. Taking a verification back before it is sent is not an
      // amendment of anything — there is nothing on the registry to amend.
      return {
        items: setActs(items, id, { ...acts, stance: acts.stance === "verify" ? null : "verify" }),
        problem: null,
      };

    // The rule lives here as well as in `verificationFor`, so a disabled button
    // is a courtesy rather than the only guard.
    case "dispute":
      return { items, problem: PROVENANCE_COPY.disputeOwnDraft };

    case "comment": {
      const body = intent.body.trim();
      if (body === "") return { items, problem: PROVENANCE_COPY.actEmpty };
      if (acts.comments.length >= MAX_ACTS_PER_ENTRY) {
        return { items, problem: PROVENANCE_COPY.actsFull };
      }
      return {
        items: setActs(items, id, {
          ...acts,
          comments: [...acts.comments, { id: crypto.randomUUID(), body }],
        }),
        problem: null,
      };
    }

    default: {
      const party = readParty(intent.party);
      if (!party) {
        return { items, problem: PROVENANCE_COPY.askInvalid };
      }
      if (
        acts.requests.some(
          (request) =>
            request.party.kind === party.kind && request.party.value === party.value,
        )
      ) {
        return { items, problem: PROVENANCE_COPY.alreadyAsked };
      }
      if (acts.requests.length >= MAX_ACTS_PER_ENTRY) {
        return { items, problem: PROVENANCE_COPY.actsFull };
      }
      return {
        items: setActs(items, id, {
          ...acts,
          requests: [...acts.requests, { id: crypto.randomUUID(), party }],
        }),
        problem: null,
      };
    }
  }
}

/** Takes back a queued comment or ask. Unknown ids return the list by identity. */
export function removeAct(
  items: ProvenanceItem[],
  id: string,
  actId: string,
): ProvenanceItem[] {
  const target = items.find((item) => item.kind === "draft" && item.entry.id === id);
  if (!target || target.kind !== "draft") return items;

  const acts = target.entry.acts;
  if (!acts.comments.some((c) => c.id === actId) && !acts.requests.some((r) => r.id === actId)) {
    return items;
  }

  return setActs(items, id, {
    ...acts,
    comments: acts.comments.filter((comment) => comment.id !== actId),
    requests: acts.requests.filter((request) => request.id !== actId),
  });
}

/** Which of the two a controller is holding, so it knows whether to POST. */
export function isDraft(items: ProvenanceItem[], id: string): boolean {
  return items.some((item) => item.kind === "draft" && item.entry.id === id);
}

function setActs(items: ProvenanceItem[], id: string, acts: PendingActs): ProvenanceItem[] {
  return setEntryField(items, id, "acts", acts);
}

/**
 * Which of the two the author typed, or null if it is neither.
 *
 * ONE FIELD IN THE FORM, not two, and the `@` decides. A radio pair would ask
 * the asker to classify what they are holding before they paste it, which is a
 * question they cannot get wrong and should not be asked — an ONDO id and an
 * email address are not confusable by eye, and nothing else is accepted here.
 *
 * The email test is deliberately loose: a shape check, not a validator. What is
 * actually deliverable is not knowable from a regex, the RPC checks the same
 * shape again, and refusing an unusual but real address would be the one
 * failure mode with no workaround — the party it locks out is exactly the one
 * this door was built for.
 */
export function readParty(typed: string): PendingParty | null {
  const trimmed = typed.trim();
  if (!trimmed) return null;

  if (trimmed.includes("@")) {
    const email = trimmed.toLowerCase();
    return EMAIL_PATTERN.test(email) ? { kind: "email", value: email } : null;
  }

  const ondoId = trimmed.toUpperCase();
  return ONDO_ID_PATTERN.test(ondoId) ? { kind: "ondoId", value: ondoId } : null;
}

/**
 * A party, as the request body names them.
 *
 * Exported so the two submit paths — the immediate one on a recorded entry and
 * the deferred one that drains a draft — cannot disagree about which field a
 * given party goes in. They already drifted once over `documentId`.
 */
export function askBody(party: PendingParty): RequestVerificationRequest {
  return party.kind === "email" ? { email: party.value } : { ondoId: party.value };
}

/**
 * '2 confirmations · 1 dispute', or null when nobody has said anything.
 *
 * Plural counts and never a verdict, exactly as `countAttestations` does it for
 * the collapsed row — this is the same rule said in the words the card uses.
 */
export function describeConfirmations(verified: number, disputed: number): string | null {
  const parts = [
    verified > 0 ? `${String(verified)} confirmation${verified === 1 ? "" : "s"}` : "",
    disputed > 0 ? `${String(disputed)} dispute${disputed === 1 ? "" : "s"}` : "",
  ].filter((part) => part !== "");

  return parts.length === 0 ? null : parts.join(" · ");
}

/**
 * The strip, from the counts and nothing else.
 *
 * One dispute takes the strip out of green however many confirmations sit
 * beside it. There is no threshold at which a majority resolves it, here or
 * anywhere — the registry shows that people disagree and stops there.
 */
export function confirmationStrip(item: ProvenanceItem): ConfirmationStrip {
  if (item.kind === "draft") {
    // A draft carries its author's own verification, filed with the entry at
    // save and never before. Saying so on the shut card keeps it visible —
    // otherwise a signature already given looks lost until the form is reopened.
    return {
      tone: "draft",
      pill: CONFIRMATION_COPY.draft,
      summary:
        item.entry.acts.stance === "verify"
          ? CONFIRMATION_COPY.draftVerified
          : CONFIRMATION_COPY.draftNote,
    };
  }

  const entry = item.entry;
  const counted = describeConfirmations(entry.verifiedByCount, entry.disputedByCount);
  const commented = entry.commentCount > 0;

  if (counted === null) {
    // Nothing has been said, but there may still be comments to read — a
    // comment is context rather than a position and changes no count.
    const author = entry.authorName ?? "its author";
    return {
      tone: "unconfirmed",
      pill: CONFIRMATION_COPY.unconfirmed,
      summary: commented
        ? `Recorded by ${author} — ${CONFIRMATION_COPY.unconfirmedNote}, ${CONFIRMATION_COPY.openHint}`
        : `Recorded by ${author} — ${CONFIRMATION_COPY.unconfirmedNote}.`,
    };
  }

  return {
    tone: entry.disputedByCount > 0 ? "disputed" : "confirmed",
    pill:
      entry.disputedByCount > 0 ? CONFIRMATION_COPY.disputed : CONFIRMATION_COPY.confirmed,
    summary: `${counted} — ${CONFIRMATION_COPY.openHint}`,
  };
}

/** The attestations and comments, once the card has been opened. */
export function toConfirmationDetail(load: ConfirmationLoad | undefined): ConfirmationDetail {
  if (!load) return { kind: "idle" };
  if (load.kind === "loading") return { kind: "loading", message: CONFIRMATION_COPY.loading };
  if (load.kind === "failed") return { kind: "failed", message: load.message };

  const attestations = load.attestations.map((attestation) => ({
    id: attestation.id,
    stance: attestation.stance,
    stanceLabel:
      attestation.stance === "verify"
        ? CONFIRMATION_COPY.confirmed
        : CONFIRMATION_COPY.disputed,
    byName: attestation.by.displayName,
    byOndoId: attestation.by.ondoId,
    date: formatActDate(attestation.createdAt),
    body: attestation.body,
    withdrawn: attestation.endedAt !== null,
    mine: attestation.mine,
  }));

  const comments = load.comments.map((comment) => ({
    id: comment.id,
    byName: comment.by.displayName,
    byOndoId: comment.by.ondoId,
    date: formatActDate(comment.createdAt),
    body: comment.body,
    withdrawn: comment.endedAt !== null,
    mine: comment.mine,
  }));

  return {
    kind: "loaded",
    attestations,
    comments,
    emptyNote:
      attestations.length === 0 && comments.length === 0 ? CONFIRMATION_COPY.none : null,
  };
}

/**
 * Every verification waiting to be filed, with the draft it belongs to.
 *
 * Exists as its own function so a test can assert that taking a verification
 * back removes it from what submit would send — not merely from what the header
 * shows. Those are two different failures and only one of them is visible.
 */
export function pendingAttestations(
  items: ProvenanceItem[],
): { localEntryId: string; stance: "verify" }[] {
  return items.flatMap((item) =>
    item.kind === "draft" && item.entry.acts.stance === "verify"
      ? [{ localEntryId: item.entry.id, stance: "verify" as const }]
      : [],
  );
}
